import {
    resolveEssenceExchangeValue,
    resolveEarnedAttributePoints,
    resolveRespecCost,
    resolveVeilAttunementPercent,
    type CharacterAttributes,
} from '@sorrem/shared';
import { useCallback } from 'react';
import type { UIPartyCharacter } from '@sorrem-client/types';
import { useRespecCharacterAttributesMutation } from '@sorrem-client/ui/store';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { useMutationError } from './use-mutation-error';

export interface AttributeRespecState {
    /** Banked essence value the next respec costs. */
    cost: number;
    /** Banked essence value the character has. */
    bankedValue: number;
    /** Attribute points the character has earned, all of which a respec reallocates. */
    earnedPoints: number;
    isRespeccing: boolean;
    /** Why the last respec failed, or null. */
    error: string | null;
    /** Resets and reallocates the attributes. Resolves to whether it succeeded. */
    respec: (attributes: CharacterAttributes) => Promise<boolean>;
}

/** Paying banked essence to reset and reallocate a character's attributes (#97). */
export function useAttributeRespec(character: UIPartyCharacter): AttributeRespecState {
    const accountId = getStoredCanonicalAccountId();
    const [respecAttributes, { isLoading: isRespeccing }] = useRespecCharacterAttributesMutation();
    const { error, refuse, run } = useMutationError();

    const respec = useCallback((attributes: CharacterAttributes): Promise<boolean> => (
        accountId === null
            ? refuse('You need to be signed in to reshape.')
            : run(() => respecAttributes({ accountId, characterId: character.id, attributes }).unwrap(), 'The reshape failed. Try again.')
    ), [accountId, character.id, refuse, respecAttributes, run]);

    return {
        cost: resolveRespecCost(character.attributeRespecCount),
        bankedValue: resolveEssenceExchangeValue(character.permanentEssence),
        earnedPoints: resolveEarnedAttributePoints(resolveVeilAttunementPercent(character.veilAttunementSpent)),
        isRespeccing,
        error,
        respec,
    };
}
